import React, { useEffect, useState } from "react";
import { Form, Formik, useField, useFormikContext } from "formik";
import { useIntl } from "react-intl";
import * as yup from "yup";
import { useKpiCatalog } from "../../services/Kpi";
import { Value } from "./CreateFilterForm";
import { FormatType, Kpi, KpiType } from "./FilterType";
import { KpiOptions } from "../../services/Alert";
import { KpiValueSetter } from "./KpiValueSetter";
import { valueDeserialize, valueSerialize } from "./utils";
import { Operator, OPERATORS } from "../../constants";
import { formInputsStyles } from "../../Configuration/styles";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { Box } from "@mui/material";

type NonNullableField<T> = { [P in keyof T]: NonNullable<T[P]> };

export interface FormValues {
  kpi: Kpi | null;
  kpiType: KpiType | null;
  operator: Operator;
  value: string | null;
}

export const DEFAULT_OPERATOR = Operator.LT;

export const greyOutTemporaryKpis = [121, 122, 123, 124, 134, 135, 136];

export const KpiField = () => {
  const inputStyles = formInputsStyles();
  const {
    setFieldTouched,
    setFieldValue,
    values: { kpiType },
  } = useFormikContext<FormValues>();
  const [field, meta, helpers] = useField("kpi");

  const { data: catalog } = useKpiCatalog();
  const kpis = kpiType
    ? catalog?.types.find((type) => type.id === kpiType.id)?.children
    : undefined;
  const onChange = (event: React.ChangeEvent<{}>, value: Value<Kpi, false>) => {
    // We change the default value when kpi is set by the user to avoid
    // overwriting default values with useEffect
    if (
      value &&
      [FormatType.BOOLEAN, FormatType.ICON].includes(value.format.type)
    ) {
      if (value.format.type === FormatType.BOOLEAN) setFieldValue("value", "1");
      setFieldValue("operator", Operator.EQ);
    } else {
      setFieldValue("value", "");
      setFieldValue("operator", DEFAULT_OPERATOR);
    }
    setFieldTouched("operator", false);
    setFieldTouched("value", false);
    helpers.setValue(value);
  };

  return (
    <Box
      className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
    >
      <CustomAutocomplete
        {...field}
        onChange={onChange}
        fullWidth
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => greyOutTemporaryKpis.includes(option.id)}
        loading={!!kpiType && !catalog}
        options={kpis || []}
        error={!!(meta.touched && meta.error)}
        helperText={(meta.touched && meta.error) || ""}
        label={"KPI"}
      />
    </Box>
  );
};

const KpiTypeField = () => {
  const inputStyles = formInputsStyles();
  const query = useKpiCatalog();
  const intl = useIntl();

  const { setFieldTouched, setFieldValue } = useFormikContext<FormValues>();
  const [field, meta, helpers] = useField("kpiType");

  const onChange = (
    event: React.ChangeEvent<{}>,
    value: Value<KpiType, false>
  ) => {
    // We change the default value when kpi is set by the user to avoid
    // unneeded reset with useEffect
    setFieldValue("kpi", null);
    setFieldValue("operator", DEFAULT_OPERATOR);
    setFieldValue("value", "", false);
    // Reset the touched when resetting values to hide errors
    setFieldTouched("kpi", false);
    setFieldTouched("operator", false);
    setFieldTouched("value", false);
    helpers.setValue(value);
  };

  return (
    <Box
      className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
    >
      <CustomAutocomplete
        {...field}
        multiple={false}
        onChange={onChange}
        fullWidth
        isOptionEqualToValue={(option: KpiType, value: KpiType) =>
          option.id === value.id
        }
        getOptionLabel={(option) => option.label}
        options={query.data?.types || []}
        error={!!(meta.touched && meta.error)}
        helperText={(meta.touched && meta.error) || ""}
        label={intl.formatMessage({ id: "KPI_TYPE" })}
      />
    </Box>
  );
};

export const CreateFilterKpiForm = ({
  model,
  onSubmit,
}: {
  model?: { target: number; options: KpiOptions };
  onSubmit: (data: { target: number; options: KpiOptions }) => void;
}) => {
  const inputStyles = formInputsStyles();
  const intl = useIntl();
  const [initialValues, setInitialValues] = useState<FormValues>({
    kpi: null,
    kpiType: null,
    operator: DEFAULT_OPERATOR,
    value: "",
  });
  const [schema] = useState(() =>
    yup
      .object({
        kpiType: yup.object().nullable().required(),
        kpi: yup.object().nullable().required(),
        operator: yup
          .mixed()
          .oneOf(OPERATORS.map((op) => op.name))
          .required(),
        value: yup
          .number()
          .required()
          .typeError(intl.formatMessage({ id: "MUST_CONTAIN_NUMBER" })),
      })
      .required()
  );

  const { data: catalog } = useKpiCatalog();

  const handleSubmitForm = (data_: FormValues) => {
    const data = data_ as NonNullableField<FormValues>;
    const options = model?.options || { granularity: 0, __typename: "kpi" };
    // Must cast because Validation does not ensure non null type...
    onSubmit({
      target: data.kpi.id,
      options: {
        ...options,
        kpi: data.kpi.id,
        parent: data.kpiType.id,
        operator: data.operator,
        value: valueDeserialize(data.kpi, data.value),
      },
    });
  };

  useEffect(() => {
    if (model && catalog) {
      const options = model.options;
      const kpiTypes = catalog.types;
      const kpiType =
        kpiTypes.find((type) => type.id === options.parent) ||
        kpiTypes[0] ||
        null;
      const kpis =
        kpiTypes.find((type) => type.id === options.parent)?.children ?? [];
      const kpi = kpis.find((k) => k.id === options.kpi) || kpis[0] || null;
      const value = kpi ? valueSerialize(kpi, options.value) : "";
      setInitialValues((prev) => ({
        ...prev,
        kpi,
        kpiType,
        operator: options.operator,
        value,
      }));
    }
  }, [model, catalog]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      validationSchema={schema}
    >
      {() => (
        <Form
          id="create-filter-form"
          style={{ minWidth: "280px", display: "flex" }}
          className={inputStyles.classes.root}
        >
          <KpiTypeField />
          <KpiField />
          <KpiValueSetter />
        </Form>
      )}
    </Formik>
  );
};
